import classNames from 'classnames'
import React, { useMemo, useState } from 'react'
import { ReactComponent as ZocksIcon } from '../../assets/ZocksIcon.svg'
import { SUPPORTED_MEETING_PROVIDERS } from '../../common/meetingProviders'
import { parseMetadata } from '../../common/util'
import { RoomSession } from '../../models/Room'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useUpdateRoomMutation, useUpdateSessionConfigMutation } from '../../store/media/media'
import { ScopedBlock } from '../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../ScopedBlock/models'
import { DropdownIconComponentProps, DropDownItemOptionComponentProps, SelectDropdown } from '../SelectDropdown/SelectDropdown'
import { AutoJoinConfirmModal } from './AutoJoinConfirmModal'
import { useAutoJoinMissingIntegrationOptions } from './useAutoJoinMissingIntegrationOptions'

export interface AutoJoinControlProps {
  session: RoomSession
}

export const AutoJoinControl: React.FC<AutoJoinControlProps> = ({ session }) => {
  const accountId = useAppSelector(selectAccountId)
  const [override, setOverride] = useState<boolean>(session.config?.autoJoinOverride ?? false)
  const [autoJoin, setAutoJoin] = useState<boolean>(session.config?.autoJoin ?? false)
  const [autoConfirmModalShow, setAutoConfirmModalShow] = useState<boolean>(false)
  const [autoJoinToChange, setAutoJoinToChange] = useState<boolean | null>(autoJoin)
  const [updateRoomSessionConfig, { isLoading: isUpdatingSessionConfig }] = useUpdateSessionConfigMutation()
  const [updateRoom, { isLoading: isUpdatingRoom }] = useUpdateRoomMutation()
  const { source } = parseMetadata(session.metadata)
  const missingIntegrationOptions = useAutoJoinMissingIntegrationOptions(session)

  const autoJoinOptions = useMemo(() => {
    const overrideMessage = override
      ? "You've manually overridden your filter for Auto-join."
      : autoJoin
        ? 'The meeting matches your filter for Auto-join.'
        : "This meeting doesn't match your Auto-join filter."
    const joinMessage = override
      ? autoJoin
        ? 'AI Assistant will join this meeting.'
        : "AI Assistant won't join this meeting."
      : autoJoin
        ? 'The AI Assistant will automatically join.'
        : 'AI Assistant will skip it.'

    const displayLabel = (
      <div className='paragraph-small'>
        {overrideMessage} {joinMessage}
      </div>
    )

    return [
      autoJoin
        ? { id: 'true', label: 'Will join', displayLabel: displayLabel }
        : {
            id: 'false',
            label: "Won't join",
            displayLabel: displayLabel,
          },
    ]
  }, [autoJoin, override])

  if (!SUPPORTED_MEETING_PROVIDERS.includes(source) || new Date(session.startedAt).getTime() <= Date.now()) {
    return null
  }

  const changeAutoJoinOfThisMeeting = async (changeTo: boolean | null) => {
    try {
      await updateRoomSessionConfig({
        accountId,
        roomId: session.roomId,
        sessionId: session.id,
        body: {
          autoJoin: changeTo,
        },
      }).unwrap()
    } finally {
      setAutoJoin(changeTo ?? false)
      setOverride((prevOverride) => !prevOverride)
      setAutoConfirmModalShow(false)
    }
  }

  const changeAutoJoinOfAllMeetings = async (changeTo: boolean | null) => {
    try {
      await updateRoom({
        accountId,
        roomId: session.roomId,
        body: {
          config: {
            autoJoin: changeTo,
          },
        },
      }).unwrap()
    } finally {
      setAutoJoin(changeTo ?? false)
      setOverride((prevOverride) => !prevOverride)
      setAutoConfirmModalShow(false)
    }
  }

  const AutoJoinIconComponent: React.FC<DropdownIconComponentProps> = ({ selectedOption }) => {
    return (
      <div
        className={classNames('auto-join-icon-container overflow-visible', {
          'auto-join': selectedOption?.id === 'true',
          'dont-join': selectedOption?.id === 'false',
        })}
      >
        <ZocksIcon width={16} height={16} className='overflow-visble' />
      </div>
    )
  }

  const AutoJoinDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ displayLabel }) => {
    return <div>{displayLabel}</div>
  }

  const handleAutoJoinChange = async () => {
    if (session.config?.isRecurring) {
      setAutoJoinToChange(override ? null : !autoJoin)
      setAutoConfirmModalShow(true)
    } else {
      await changeAutoJoinOfThisMeeting(override ? null : !autoJoin)
    }
  }

  const AutoJoinDropdownFooter = () => {
    const ctaMessage = override ? 'Use Auto-join filter instead.' : autoJoin ? "Don't join this meeting" : 'Join this meeting anyway'
    return (
      <div role='button' className='mt-2 button-small w-100 zocks-text-primary cursor-pointer' onClick={handleAutoJoinChange}>
        {ctaMessage}
      </div>
    )
  }

  return (
    <>
      <AutoJoinConfirmModal
        show={autoConfirmModalShow}
        onHide={() => setAutoConfirmModalShow(false)}
        changeTo={autoJoinToChange}
        thisMeetingLoading={isUpdatingSessionConfig}
        allMeetingsLoading={isUpdatingRoom}
        onThisMeeting={changeAutoJoinOfThisMeeting}
        onAllMeetings={changeAutoJoinOfAllMeetings}
      />
      <ScopedBlock
        scope={{
          scope: 'room_session:update',
          superScope: ['room_session:admin', 'room:admin'],
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <SelectDropdown
          options={missingIntegrationOptions ?? autoJoinOptions}
          onOptionSelected={() => {}}
          IconComponent={AutoJoinIconComponent}
          DropDownItemOptionComponent={AutoJoinDropdownItemOption}
          DropdownFooterComponent={!missingIntegrationOptions ? AutoJoinDropdownFooter : undefined}
        />
      </ScopedBlock>
    </>
  )
}
